(ns manage.ad-variant-preview.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/ToggleButton$default" :as ToggleButton]
            ["@mui/material/ToggleButtonGroup$default" :as ToggleButtonGroup]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["react-use/lib/useMeasure$default" :as useMeasure]
            ["react-window" :refer [VariableSizeList]]
            ["src/svg/artwork/masonry.svg$default" :as Masonry]
            ["src/svg/artwork/no-access.svg$default" :as NoAccess]
            ["src/svg/artwork/not-found.svg$default" :as NotFound]
            ["src/svg/editor/formatting/list.svg$default" :as ViewList]
            ["src/svg/shapes/square/square-inset-center-dashed.svg$default" :as SquareInsetDashed]
            ["src/svg/wpp/content-actions/color.svg$default" :as Color]
            [cljs-bean.core :refer [->clj]]
            [common.mui :as mui]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.patterns.loading-view :refer [loading-view]]
            [common.queries :refer [use-infinite-query use-query]]
            [common.uix.hooks :as uix.hooks]
            [common.widgets.overlay-page :as overlay-page]
            [common.widgets.page :as page]
            [manage.ad-variant-preview.bottom-panel :refer [bottom-panel]]
            [manage.ad-variant-preview.comments]
            [manage.ad-variant-preview.data :as data]
            [manage.ad-variant-preview.db :as db]
            [manage.ad-variant-preview.hooks :as hooks]
            [manage.ad-variant-preview.meta :refer [preview-format-selector]]
            [manage.ad-variant-preview.share :as share]
            [manage.ad-variant-preview.variant-drawer :refer [drawer]]
            [manage.ad-variant-preview.viewport :as viewport]
            [manage.ad-variant-preview.widgets.preview-item :refer [preview-item]]
            [manage.ad-variant.data :as variant-data]
            [manage.ad-variant.page.columns :as columns]
            [manage.ad-variant.queries :as q]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui ^:memo header [{:keys [on-close]}]
  ($ overlay-page/header {:on-close on-close :title "Preview"}
     ($ Stack
        {:direction "row"
         :alignItems "center"
         :spacing 2}
        ($ Button
           {:variant "subtle"
            :onClick #(rf/dispatch [:ocp/open {:id :comments/ocp
                                               :props {:comment-mode? false}}])}
           "Activity log")
        ($ Button {:variant "contained"
                   :onClick (fn [_e] (rfe/set-query #(assoc % :share true)))}
           "Share"))))

(defui ^:memo top-panel [{:keys [mode set-mode show-preview-format? preview-format set-preview-format]}]
  (let [icon-props {:width 20 :height 20}]
    ($ Stack
       {:direction "row"
        :justifyContent "space-between"
        :alignItems "center"
        :sx (clj->js (when show-preview-format?
                       {:pb 2 :borderBottom "1px solid" :borderColor "divider"}))}
       (if show-preview-format?
         ($ preview-format-selector {:format preview-format :set-format set-preview-format})
         ($ Box))
       ($ ToggleButtonGroup
          {:direction "row"
           :value mode
           :exclusive true
           :onChange (fn [_e value]
                       (when value
                         (set-mode value)))}
          ($ Tooltip
             {:title "List view"}
             ($ ToggleButton
                {:value "list"}
                ($ ViewList icon-props)))
          ($ Tooltip
             {:title "Gallery view"}
             ($ ToggleButton
                {:value "gallery"}
                ($ SquareInsetDashed icon-props)))
          ($ Tooltip
             {:title "Canvas view"}
             ($ ToggleButton
                {:value "canvas"}
                ($ Color icon-props)))))))

(defui empty-selection []
  ($ empty-state-view
     {:artwork Masonry
      :title "Select variants to preview"
      :description "Click the variants in the left hand filter panel to view them here."}))

(defn get-variant-dimensions [item-scale variant]
  (let [{:keys [components dimensions ad-type]} variant
        main-component (variant-data/get-main-component components
                                                        ad-type)
        [w h] (data/get-card-dimensions
               {:ad-type ad-type
                :component main-component
                :template-dimensions dimensions
                :item-scale item-scale})]
    {:w w
     :h (+ h (if (variant-data/meta? ad-type)
               380
               110))}))

(defn build-rows-for-virtualization
  [grouped-variants container-width item-scale]
  (let [padding 24
        pack-row (fn [{:keys [rows current-row]} variant]
                   (let [{:keys [w h]} (get-variant-dimensions item-scale variant)
                         needed-width (+ (:width current-row) padding w)]
                     (if (<= needed-width container-width)
                       {:rows rows
                        :current-row {:variants (conj (:variants current-row []) variant)
                                      :width needed-width
                                      :max-height (max (:max-height current-row 0) h)}}
                       {:rows (conj rows current-row)
                        :current-row {:variants [variant]
                                      :width w
                                      :max-height h}})))
        finalize-rows (fn [{:keys [rows current-row]}]
                        (cond-> rows
                          (seq (:variants current-row)) (conj current-row)))
        row->data (fn [{:keys [variants max-height]}]
                    {:type :variants
                     :variants (vec variants)
                     :height (+ max-height padding)})]
    (->> grouped-variants
         (mapcat (fn [[ad-type group-variants]]
                   (let [header {:type :header
                                 :ad-type ad-type
                                 :count (count group-variants)
                                 :height 40}
                         content (->> group-variants
                                      (reduce pack-row {:rows [] :current-row {}})
                                      finalize-rows
                                      (mapv row->data))]
                     (cons header content))))
         (into []))))

(defui virtualized-row [{:keys [style index data]}]
  (let [{:keys [product-overrides-ref]} (viewport/use-context)]
    (when-let [{:keys [ad-type count variants] :as row} (get-in data [:rows index])]
      ($ :div {:style style}
         (case (:type row)
           :header
           ($ Stack {:direction "row"
                     :alignItems "center"}
              ($ Typography {:fontWeight 700}
                 (variant-data/ad-type-title ad-type))
              ($ Typography {:sx #js {:color "grey.40"
                                      :mx 1}}
                 " • ")
              ($ Typography {:sx #js {:color "grey.40"}}
                 count " Ad variant" (when (not= count 1) "s")))

           :variants
           ($ Stack {:direction "row"
                     :gap 3}
              (for [{:keys [uuid] :as variant} variants]
                ($ preview-item (assoc data :key uuid :variant variant
                                       :product-overrides @product-overrides-ref)))))))))

(defn virtualized-row* [props]
  ($ virtualized-row (select-keys (->clj props) [:style :index :data])))

(defn get-focused-item-details
  [rows {:keys [visibleStartIndex visibleStopIndex]}]
  (let [middle-row-index (/ (+ visibleStartIndex visibleStopIndex) 2)
        {middle-row-type :type} (get rows middle-row-index)
        middle-row-index* (if (= middle-row-type :variant)
                            middle-row-index
                            (inc middle-row-index))
        {:keys [variants]} (get rows middle-row-index*)
        middle-variant (get variants (/ (count variants) 2))]
    {:dimensions (get-variant-dimensions 1 middle-variant)
     :row-index middle-row-index*}))

(defui virtualized-list-items
  [{:keys [visible-variants
           focused-item-uuid
           set-video-status
           video-controls
           mode
           review
           on-current-card-video-change]}]
  (let [{:keys [pan-area-el-ref item-scale focused-item-ref]} (viewport/use-context)
        list-ref (uix/use-ref nil)
        [container-ref container-bounds] (useMeasure)
        container-width (.-width container-bounds)
        container-height (.-height container-bounds)

        grouped-variants (uix/use-memo #(data/group-variants visible-variants mode)
                                       [visible-variants mode])
        rows (uix/use-memo
              #(build-rows-for-virtualization grouped-variants container-width item-scale)
              [grouped-variants container-width item-scale])
        get-row-height (uix/use-callback (fn [index] (get-in rows [index :height]))
                                         [rows])]
    (uix/use-effect #(when-some [list ^js @list-ref]
                       (.resetAfterIndex list 0))
                    [rows container-width item-scale])
    ($ :div {:ref container-ref
             :style {:width "100%" :height "100%"}}
       ($ VariableSizeList {:ref list-ref
                            :outerRef pan-area-el-ref
                            :height container-height
                            :width container-width
                            :onItemsRendered
                            (fn [^js params]
                              (reset! focused-item-ref
                                      (get-focused-item-details rows (->clj params))))
                            :itemCount (count rows)
                            :itemSize get-row-height
                            :itemData #js {:rows rows
                                           :set-video-status set-video-status
                                           :video-controls video-controls
                                           :review review
                                           :focused-item-uuid focused-item-uuid
                                           :on-current-card-video-change on-current-card-video-change}
                            :overscanCount 2}
          virtualized-row*))))

(defui ^:memo list-items
  [{:keys [visible-variants mode] :as props}]
  (let [{:keys [pan-area-el-ref product-overrides-ref]} (viewport/use-context)]
    (if (= "gallery" mode)
      (when-let [variant (first visible-variants)]
        ($ Stack {:sx #js {:overflow "scroll"
                           :width "100%"
                           :height "100%"}
                  :alignItems "safe center"
                  :ref pan-area-el-ref
                  :justifyContent "safe center"}
           ($ Box
              ($ preview-item (assoc props :variant variant
                                     :product-overrides @product-overrides-ref)))))
      ($ virtualized-list-items props))))

(defui ^:memo canvas-item-grid
  [{:keys [visible-variants
           focused-item-uuid
           set-item-places
           set-video-status
           video-controls
           review
           on-current-card-video-change]}]
  (let [variants (uix/use-memo (fn []
                                 (data/group-variants visible-variants "canvas"))
                               [visible-variants])
        {:keys [product-overrides-ref]} (viewport/use-context)]
    ($ Stack
       {:direction "column"
        :spacing 4}
       (for [[content-id group-variants] variants]
         ($ Stack
            {:direction "row"
             :key content-id
             :spacing 4}
            (for [{:keys [uuid] :as variant} group-variants]
              ($ preview-item
                 {:key uuid
                  :variant variant
                  :review review
                  :set-item-places set-item-places
                  :set-video-status set-video-status
                  :video-controls video-controls
                  :focused (= uuid focused-item-uuid)
                  :on-current-card-video-change on-current-card-video-change
                  :product-overrides @product-overrides-ref})))))))

(defui canvas-view
  [{:keys [visible-variants
           focused-item-uuid
           set-item-places
           set-video-status
           video-controls
           viewport
           on-current-card-video-change]}]
  (let [{:keys [pan-area-el-ref] :as ctx} (viewport/use-context)
        [pan-active set-pan-active] (uix/use-state false)]
    (viewport/use-d3-zoom ctx set-pan-active)
    ($ viewport/pan-area
       {:pan-area-el-ref pan-area-el-ref
        :pan-active pan-active
        :viewport viewport}
       ($ canvas-item-grid
          {:visible-variants visible-variants
           :set-item-places set-item-places
           :set-video-status set-video-status
           :video-controls video-controls
           :focused-item-uuid focused-item-uuid
           :on-current-card-video-change on-current-card-video-change}))))

(defui preview [{:keys [set-item-places set-video-status focused-item-uuid
                        mode
                        video-controls
                        preview-area-measure-ref
                        viewport
                        visible-variants
                        review
                        products-hash ; it used to force component re-render when products updated
                        on-current-card-video-change]}]
  ($ Stack
     {:direction "column"
      ;; Grow to use available space height wise,
      ;; bottom space is used by bottom-panel.
      :flexGrow 1
      :ref preview-area-measure-ref
      :sx #js {;; Required for correct pan-area sizing
               :overflow "hidden"
               :mt 3
               ;; For debug-cross positioning
               :position "relative"}}
     (if (empty? visible-variants)
       ($ empty-selection)
       (case mode
         "canvas"
         ($ canvas-view
            {:visible-variants visible-variants
             :review review
             :set-item-places set-item-places
             :set-video-status set-video-status
             :video-controls video-controls
             :focused-item-uuid focused-item-uuid
             :viewport viewport
             :on-current-card-video-change on-current-card-video-change})

         ($ list-items
            {:mode mode
             :visible-variants visible-variants
             :review review
             :set-item-places set-item-places
             :set-video-status set-video-status
             :video-controls video-controls
             :products-hash products-hash
             :focused-item-uuid focused-item-uuid
             :on-current-card-video-change on-current-card-video-change})))
     #_($ viewport/debug-cross)))

(defui content [{:keys [is-fetching-variants all-variants accessed-via-share-link
                        on-close shared-advertiser-id share-token shared-product-overrides]}]
  (let [{share-open :share, review-mode? :review}
        (use-subscribe [:router/query-parameters])

        {:keys [adset-id]} (use-subscribe [:url-context])
        {review :data} (use-query (q/review-query {:adset-id adset-id}))

        ;; TODO: Just use use-subscribe and dispatcher where these are used??
        [filters set-filters]
        (uix.hooks/use-state {:initial-value (assoc (if review-mode?
                                                      data/empty-filters-reviewer
                                                      data/empty-filters)
                                                    :search "")
                              :path [::db/state :filters]})

        [mode set-mode]
        (uix.hooks/use-state {:initial-value "list"
                              :path [::db/state :mode]})

        [preview-format set-preview-format]
        (uix.hooks/use-state {:initial-value (first data/preview-formats)
                              :path [::db/state :preview-format]})

        ;; These are defined here so that the value and fn look like use-state
        ;; use to the children components.
        visible-selection (use-subscribe [::db/visible])

        current-ad-types (uix/use-memo (fn []
                                         (set (map :ad-type all-variants)))
                                       [all-variants])
        [has-video-components
         has-meta-components]
        (uix/use-memo (fn []
                        [(some (fn [ad-type] (= :video (variant-data/get-component-type ad-type))) current-ad-types)
                         (some variant-data/meta? current-ad-types)])
                      [current-ad-types])

        filtered-variants
        (uix/use-memo (fn []
                        (data/get-filtered-variants all-variants filters))
                      ;; identity comparison
                      [all-variants filters])

        visible-variants
        (uix/use-memo (fn []
                        (data/get-selected-variants filtered-variants visible-selection))
                      [filtered-variants visible-selection])

        show-preview-format? (uix/use-memo #(data/any-multi-placement-carousel? visible-variants)
                                           [visible-variants])

        ;;
        ;; Viewport setup
        ;;
        ;; Because the viewport state is here, this component is rendered quite often,
        ;; and children need (in many cases) to be memoized so they don't render on
        ;; every mouse move event.

        ;; For sending commands (like reload display ads, video controls) to all/many previews.
        event-target (uix/use-ref (js/EventTarget.))
        pan-area-el-ref (uix/use-ref nil)

        [preview-area-measure-ref preview-area-measure] (useMeasure)
        ;; width-area, height-area
        w-a (.-width preview-area-measure)
        h-a (.-height preview-area-measure)

        [[_x _y scale :as viewport] set-viewport]
        (uix.hooks/use-state {:initial-value [0 0 1.0]
                              :path [::db/state :pos]})

        ;; Item-places need to just trigger minimap render,
        ;; otherwise this is just for side effects so
        ;; this could be optimized to use something else than use-state.
        [item-places set-item-places] (uix/use-state {})
        item-places-ref (uix/use-ref nil)
        focused-item-ref (uix/use-ref nil)
        [focused-item-uuid set-focused-item-uuid] (uix/use-state {})

        d3-zoom-ref (uix/use-ref nil)
        ;; ctx value should have a stable value
        item-scale (when (not= "canvas" mode)
                     scale)

        video-status-ref (uix/use-ref nil)
        [video-status set-video-status] (uix/use-state {})
        [video-controls set-video-controls] (uix/use-state {:auto-replay false
                                                            ;; NOTE: for testing
                                                            ;; :selection true
                                                            ;; :start 7
                                                            ;; :end 14
                                                            :toggle-timeline-selection-editing false
                                                            :start nil
                                                            :end nil})

        product-overrides-ref (uix/use-ref {})
        [product-overrides set-product-overrides] (uix/use-state {})

        on-product-selected (fn [product selector-id item-position products selector-products]
                              (let [current-overrides (or (get products selector-id) (-> (repeat 10 nil) vec))
                                    selector-product-with-overrides (mapv #(or %2 %1) selector-products current-overrides)
                                    combined-overrides {selector-id
                                                        (assoc selector-product-with-overrides item-position product)}]
                                (set-product-overrides (merge products combined-overrides))
                                (rf/dispatch [:modal/close])))

        ctx (uix/use-memo (fn []
                            {:d3-zoom-ref d3-zoom-ref
                             :pan-area-el-ref pan-area-el-ref
                             :focused-item-ref focused-item-ref
                             :item-places-ref item-places-ref
                             :event-target @event-target
                             :w-a w-a
                             :h-a h-a
                             :set-viewport set-viewport
                             :set-video-controls set-video-controls
                             :mode mode
                             :item-scale item-scale
                             :preview-format preview-format
                             :set-preview-format set-preview-format
                             :product-overrides-ref product-overrides-ref
                             :video-status-ref video-status-ref
                             :accessed-via-share-link accessed-via-share-link
                             :on-product-selected on-product-selected
                             :shared-advertiser-id shared-advertiser-id
                             :shared-product-overrides shared-product-overrides
                             :share-token share-token
                             :min-zoom (if has-meta-components
                                         0.4
                                         0.25)})
                          ;; d3-zoom-ref is stable
                          ^:lint/disable
                          [w-a h-a mode scale preview-format])]

    (reset! video-status-ref video-status)
    (reset! item-places-ref item-places)
    (reset! product-overrides-ref product-overrides)

    ;; TODO: initial opening of view with selected items doesn't focus
    ;; on the first item correctly?

    ;; Order of effects is important, this should run before find-closest-item.
    ;; Alternative is to handle this logic in set-item-places, but separating
    ;; it to its own effect probably makes sense.
    (let [previous (uix/use-ref nil)]
      (uix/use-effect (fn []
                        (let [old @previous
                              uuid (:uuid @focused-item-ref)
                              new (get item-places uuid)]
                          (reset! previous {:uuid uuid
                                            :pos new})
                          ;; First item selected, center the view on it.
                          ;; On shadow-cljs reload gets called with w-a/h-a 0, ignore those wor better dev exp.
                          (when (and (nil? (:uuid old)) (pos? w-a))
                            (viewport/fit-to-view ctx))
                          (when (= (:uuid old) uuid)
                            (viewport/keep-focused-item-position d3-zoom-ref pan-area-el-ref (:pos old) new))))
                      ;; d3-zoom-ref, ctx are stable
                      ^:lint/disable
                      [item-places]))

    (uix/use-effect (fn find-closest-item []
                      ;; For list mode the focused item is calculated in `:onItemsRendered`
                      ;; of the virtualized list
                      (when-not (= "list" mode)
                        (let [focused-item (viewport/find-closest-item viewport item-places w-a h-a)]
                          (reset! focused-item-ref focused-item)
                          (set-focused-item-uuid (:uuid focused-item)))))
                    [mode viewport item-places w-a h-a])

    ;; When switching into gallery mode, change the visible-selection to only include
    ;; the first selected variant.
    (let [no-selected (empty? visible-variants)
          many-selected (boolean (next visible-variants))]
      (uix/use-effect (fn []
                        (when (and (= "gallery" mode)
                                   (or no-selected
                                       many-selected))
                          (let [vs (if (seq visible-variants)
                                     visible-variants
                                     filtered-variants)
                                ;; Sort the same way as in sidebar
                                vs (data/list-variants vs)
                                {:keys [uuid]} (first vs)]
                            (rf/dispatch [::db/set-visible-item uuid]))))
                      ^:lint/disable
                      [mode no-selected many-selected]))

    ($ viewport/CtxProvider
       {:value ctx}
       ($ :<>
          (when has-meta-components
            ($ mui/font-family {:font-family-name "Roboto"}))
          (when-not accessed-via-share-link
            ($ header {:on-close on-close}))
          ($ page/content-padding
             {:sx #js {:overflow "hidden"
                       :flexDirection "row"
                       :backgroundColor "grey.90"
                       :gap 3
                       :pt 2}}
             ($ drawer
                {:is-fetching-variants is-fetching-variants
                 :visible-selection visible-selection
                 :all-variants all-variants
                 :filtered-variants filtered-variants
                 :filters filters
                 :set-filters set-filters
                 :mode mode})
             ($ Stack
                {:direction "column"
                 :sx #js {:flex 1
                          ;; Need to ignore overflow to keep the width
                          ;; stable when selected item amount changes.
                          :overflowX "hidden"}}
                ($ top-panel
                   {:mode mode
                    :set-mode set-mode
                    :show-preview-format? show-preview-format?
                    :preview-format preview-format
                    :set-preview-format set-preview-format})
                ($ preview
                   {:mode mode
                    :visible-variants visible-variants
                    :review review
                    :viewport viewport
                    :set-item-places set-item-places
                    :set-video-status set-video-status
                    :video-controls video-controls
                    :focused-item-uuid focused-item-uuid
                    :products-hash (hash product-overrides)
                    :preview-area-measure-ref preview-area-measure-ref})
                ($ bottom-panel
                   {:scale scale
                    :item-places item-places
                    :video-status video-status
                    :video-controls video-controls
                    :has-video-components has-video-components})))
          ($ share/share-dialog
             {:open share-open
              :ad-variants filtered-variants
              :visible-variants visible-variants
              :product-overrides product-overrides})))))

(defui error-page [{:keys [error-code]}]
  (let [props (case error-code
                "link-expired" {:artwork NoAccess
                                :title "Access to this page has expired"
                                :description ($ Typography "This page is only available for 30 days, ask the owner to resend a new link.")}
                "link-revoked" {:artwork NoAccess
                                :title "Access to this page has been revoked"
                                :description ($ Typography "This page is now unavailable, ask the owner to resend a new link.")}
                "invalid-link" {:artwork NotFound
                                :title "Link not found"
                                :description ($ Typography {:width 480 :textAlign "center"}
                                                "Either this preview doesn't exist or you don't have permission to view it.
                                                 Ask the owner to verify the link and/or update permissions.")}
                {:artwork NotFound
                 :title "Error"
                 :description "We've encountered an error, please try again later."})]
    ($ empty-state-view props)))

(defui shared-ad-variant-preview-page []
  (let [{:keys [share-link-id]} (use-subscribe [:url-context])

        {token-error :isError token-loading? :isLoading
         {:keys [share-token]} :data
         {:keys [error-code]} :error} (use-query (q/share-token-query {:share-link-id share-link-id}))
        {meta-error :isError meta-loading? :isLoading
         {:share-link/keys [share-constraints entity-ids]} :data}
        (use-query (q/share-link-meta-data-query {:share-link-id share-link-id}))
        advertiser-id (:advertiser-id entity-ids)

        {:keys [all-variants is-fetching-variants]}
        (hooks/use-fetch-and-select-all-ad-variants
         (use-infinite-query (q/shared-ad-variants-query
                              share-token
                              share-constraints)))

        product-overrides (:products share-constraints)]
    ($ overlay-page/overlay-page
       (cond
         (or token-loading? meta-loading?) ($ loading-view)
         (or token-error meta-error) ($ error-page {:error-code error-code})
         :else ($ content {:is-fetching-variants is-fetching-variants
                           :all-variants all-variants
                           :shared-advertiser-id advertiser-id
                           :shared-product-overrides product-overrides
                           :share-token share-token
                           :accessed-via-share-link true})))))

(def ad-variant-preview-page
  "This is used with Fade transition in the parent page, so the
   component needs to support ref and use given style to
   apply the Fade transition styles."
  (uix/as-react
   (fn [{:keys [ref style onClose]}]
     (let [url-context (use-subscribe [:url-context])
           {:keys [all-variants is-fetching-variants]}
           (hooks/use-fetch-and-select-all-ad-variants
            (use-infinite-query (q/search-ad-variants-query
                                 url-context
                                 {:field->sort-field columns/field->sort-field}
                                 {:sort-model #js [#js {:field "contentId", :sort "asc"}]
                                  :pagination-model #js {:pageSize 100}})))]

       ($ overlay-page/overlay-page {:ref ref :style style}
          ($ content
             {:is-fetching-variants is-fetching-variants
              :all-variants all-variants
              :accessed-via-share-link false
              :on-close onClose}))))))
